import React, { useState } from "react";
import { DotLegalButton, DotLegalEmptyState, DotLegalTableWithControls, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { Box, IconButton, TableCell, TableRow } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { usePoliciesQuery } from "../../../common/hooks/useSelectableItemQueries.ts";
import { PolicyRow } from "../../../processingActivity/policiesStep/PoliciesStep.types.ts";
import PolicySelectorDialog from "../../../processingActivity/policiesStep/policySelectorDialog/PolicySelectorDialog.tsx";
import DotLegalStepHeader from "../../../common/components/dotLegalStepHeader/DotLegalStepHeader.tsx";
import Rocket from "../../../processingActivity/processingActivities/rocket.svg?react";

export interface SystemPoliciesFieldProps {
    policies: Array<string>;
    onChange: (policies: Array<string>) => void;
    readOnly?: boolean;
    disabled: boolean;
    isPreview: boolean;
    errorText?: string;
}

function SystemPoliciesField(props: SystemPoliciesFieldProps) {
    const { translateString, translatePolicyTypes } = useTranslation();
    const policiesQuery = usePoliciesQuery(!props.isPreview);
    const [openDialog, setOpenDialog] = useState(false);

    const getSelectedPolicies = () => {
        if (policiesQuery.data) {
            return policiesQuery.data.filter((x) => props.policies.includes(x.id));
        }
        return [];
    };

    const getAvailablePolicies = () => {
        if (policiesQuery.data) {
            return policiesQuery.data.filter((x) => !props.policies.includes(x.id));
        }
        return [];
    };

    const onAddPoliciesClick = (policiesToAdd: Array<string>) => {
        props.onChange(props.policies.concat(policiesToAdd));
    };

    const onRemovePolicy = (id: string) => {
        props.onChange(props.policies.filter((item) => item !== id));
    };

    const headers: Array<ITableHeader<PolicyRow>> = [
        { property: "name", text: translateString("policy"), align: "left", showOnMobile: true },
        { property: "type", text: translateString("type"), align: "left", showOnMobile: true },
        { property: null, text: "" },
    ];

    return (
        <>
            <DotLegalStepHeader toolTipText={translateString("systemPolicyHeaderTooltip")} bottomMargin>
                {translateString("systemPolicyHeader")}
            </DotLegalStepHeader>

            {props.policies.length > 0 ? (
                <DotLegalTableWithControls
                    getUserSpecificPageLength={() => 10}
                    hideRowsPerPage
                    emptyText={translateString("noPoliciesMatchesQuery")}
                    labelRowsPerPage={translateString("showEntites")}
                    labelSearch={translateString("search")}
                    noOptionsLabel={translateString("noOptions")}
                    paginationLabelOf={translateString("labelOf")}
                    headers={headers}
                    defaultOrderBy={"name"}
                    data={getSelectedPolicies()}
                    defaultOrder={"asc"}
                    isLoading={false}
                    renderRow={(row) => (
                        <TableRow key={row.id}>
                            <TableCell>{row.name}</TableCell>
                            <TableCell>{translatePolicyTypes(row.type.toString())}</TableCell>
                            <TableCell align="right">
                                {!props.readOnly && !props.disabled && (
                                    <IconButton size="small" onClick={() => onRemovePolicy(row.id)}>
                                        <CloseIcon sx={(theme) => ({ fill: theme.palette.error.main })} />
                                    </IconButton>
                                )}
                            </TableCell>
                        </TableRow>
                    )}
                />
            ) : (
                <DotLegalEmptyState icon={<Rocket />} text={translateString("policiesStepNoPolicies")} />
            )}

            {!props.readOnly && !props.disabled && (
                <Box sx={{ marginTop: 2 }}>
                    <DotLegalButton onClick={() => setOpenDialog(true)} buttonType="secondary">
                        {translateString("addPolicy")}
                    </DotLegalButton>
                </Box>
            )}

            {props.errorText && (
                <Box
                    sx={(theme) => ({
                        fontSize: theme.typography.pxToRem(12),
                        color: theme.palette.error.main,
                        textAlign: "center",
                        marginTop: theme.spacing(2),
                    })}
                >
                    {props.errorText}
                </Box>
            )}

            {openDialog && !props.readOnly && (
                <PolicySelectorDialog
                    header={translateString("addPolicyToSystem")}
                    onDialogClose={() => setOpenDialog(false)}
                    isLoading={policiesQuery.isLoading}
                    policies={getAvailablePolicies()}
                    onAddPoliciesClick={onAddPoliciesClick}
                />
            )}
        </>
    );
}

export default SystemPoliciesField;
